import { useMemo, useState, useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useReactTable, getCoreRowModel, flexRender } from '@tanstack/react-table'
import { truncateToMinuteFloor, minutesBetween } from '@/lib/dateUtils'
import type { RecoveryRunbookTask } from '@/types/entities'
import { formatSecondsToDHM } from '@/features/objects/utils/formatTime'
import { Button } from '@/components/ui/button'
import { Check, Play, Copy } from 'lucide-react'
import { EditableStatusCell, EditableOwnerCell, ReadOnlyOwnerDisplay, createRunbookRecoveryColumns, computeForecastTimesMap, DropdownOpenRowIndexContext, ExpandedRowsContext } from './columns-runbook-recovery'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { CardTableViewToggle } from '@/components/ui/card-table-view-toggle'
import { VersionUrlsTable } from './VersionUrlsTable'
import { TranslatableText } from '@/components/ui/TranslatableText'

interface RunbookRecoveryTableProps {
    tasks: RecoveryRunbookTask[]
    forecastStartTime: Date | null
    onSave: (tasks: RecoveryRunbookTask[]) => void
    requestID: number | string
    onPauseRefetchChange?: (pause: boolean) => void
    readOnly?: boolean
    onTaskIssue?: (task: RecoveryRunbookTask) => void
}

type TaskStatusDisplay = 'complete' | 'inProgress' | 'waiting' | 'ready' | 'issue'

function getTaskStatusDisplay(task: RecoveryRunbookTask): TaskStatusDisplay {
    const status = (task.taskStatus || 'Pending').toLowerCase()
    const waitGo = (task.waitGo || 'WAIT').toUpperCase()

    if (status === 'completed') return 'complete'
    if (status === 'started') return 'inProgress'
    if (status === 'issue') return 'issue'
    if (status === 'pending' && waitGo === 'WAIT') return 'waiting'
    if (status === 'pending' && waitGo === 'GO') return 'ready'
    return 'ready'
}

function ForecastIndicator({
    task,
    forecastFinishMs,
    t,
}: {
    task: RecoveryRunbookTask
    forecastFinishMs: number | null
    t: (key: string, opts?: Record<string, unknown>) => string
}) {
    const plannedFinish = task.plannedFinish ? new Date(task.plannedFinish) : null
    const forecastFinish = forecastFinishMs != null ? new Date(forecastFinishMs) : null

    if (!plannedFinish || !forecastFinish) {
        return (
            <div className="w-[100px] flex-shrink-0 text-right">
                <span className="text-xs text-gray-400">-</span>
            </div>
        )
    }

    const deviationMinutes = Math.round(
        (plannedFinish.getTime() - forecastFinish.getTime()) / 60000
    )
    const isAhead = deviationMinutes > 0
    const isBehind = deviationMinutes < 0
    const deviationFormatted = formatSecondsToDHM(Math.abs(deviationMinutes) * 60)

    const label =
        deviationMinutes === 0
            ? t('recoverRequests.forecastOnSchedule')
            : isAhead
              ? t('recoverRequests.forecastAhead', { time: deviationFormatted })
              : t('recoverRequests.forecastBehind', { time: deviationFormatted })

    const isCompleted = !!task.actualFinish
    const tooltip =
        deviationMinutes === 0
            ? isCompleted
                ? t('recoverRequests.finishedOnSchedule')
                : t('recoverRequests.expectedOnSchedule')
            : isCompleted
              ? isAhead
                  ? t('recoverRequests.finishedAhead', { time: deviationFormatted })
                  : t('recoverRequests.finishedBehind', { time: deviationFormatted })
              : isAhead
                ? t('recoverRequests.expectedAhead', { time: deviationFormatted })
                : t('recoverRequests.expectedBehind', { time: deviationFormatted })

    const clampRange = 60
    const clampedDev = Math.max(-clampRange, Math.min(clampRange, deviationMinutes))
    const positionPercent = Math.min(100, Math.max(0, 50 + (clampedDev / clampRange) * 50))

    return (
        <div
            className="w-[100px] flex-shrink-0 flex flex-col items-end gap-0.5"
            title={tooltip}
        >
            <span
                className={`text-xs font-medium ${
                    isAhead ? 'text-emerald-600' : isBehind ? 'text-red-500' : 'text-gray-500'
                }`}
            >
                {label}
            </span>
            <div className="relative h-1.5 w-full max-w-[80px] rounded-full bg-gray-200 overflow-visible">
                <div
                    className="absolute top-0 left-1/2 w-px h-full bg-gray-400 -translate-x-px"
                    aria-hidden
                />
                <div
                    className={`absolute top-1/2 h-2 w-2 rounded-full -translate-y-1/2 -translate-x-1/2 ${
                        isAhead ? 'bg-emerald-500' : isBehind ? 'bg-red-500' : 'bg-blue-500'
                    }`}
                    style={{ left: `${positionPercent}%` }}
                />
            </div>
        </div>
    )
}

function TimelineNode({
    task,
    index,
    onTaskChange,
    t,
    readOnly = false,
}: {
    task: RecoveryRunbookTask
    index: number
    onTaskChange: (index: number, field: keyof RecoveryRunbookTask, value: any) => void
    t: (key: string) => string
    readOnly?: boolean
}) {
    const display = getTaskStatusDisplay(task)
    const taskNo = task.taskNo ?? '-'
    const isReady = display === 'ready' && !readOnly

    const nodeClasses = {
        complete: 'bg-emerald-500 text-white',
        inProgress: 'bg-blue-500 text-white ring-4 ring-blue-100 ring-offset-2',
        waiting: 'bg-amber-500 text-white',
        ready: 'bg-purple-500 text-white animate-runbook-ready-ring',
        issue: 'bg-red-500 text-white',
    }

    const handleNodeClick = (e: React.MouseEvent) => {
        if (!isReady) return
        e.stopPropagation()
        onTaskChange(index, 'taskStatus', 'Started')
    }

    return (
        <div
            role={isReady ? 'button' : undefined}
            tabIndex={isReady ? 0 : undefined}
            onClick={handleNodeClick}
            onKeyDown={isReady ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onTaskChange(index, 'taskStatus', 'Started') } } : undefined}
            className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-transform ${nodeClasses[display]} ${isReady ? 'cursor-pointer hover:scale-110' : ''}`}
            aria-label={t('objects.runbook.taskNo') + ' ' + taskNo}
        >
            {display === 'complete' ? (
                <Check className="h-5 w-5" aria-hidden />
            ) : display === 'ready' ? (
                <span className="animate-runbook-ready-pulse inline-flex">
                    <Play className="h-5 w-5 ml-0.5" aria-hidden fill="currentColor" />
                </span>
            ) : (
                <span>{taskNo}</span>
            )}
        </div>
    )
}

function parseDependencyNumbers(deps: string): number[] {
    if (!deps?.trim()) return []
    return deps
        .split(/[,\s]+/)
        .map((s) => parseInt(s.trim(), 10))
        .filter((n) => !isNaN(n) && n > 0)
}

function TaskCard({
    task,
    index,
    isExpanded,
    onExpand,
    onTaskChange,
    onDropdownOpenChange,
    forecastFinishMs,
    copyToClipboard,
    copiedId,
    allTasks,
    t,
    readOnly = false,
}: {
    task: RecoveryRunbookTask & { _index: number }
    index: number
    isExpanded: boolean
    onExpand: () => void
    onTaskChange: (index: number, field: keyof RecoveryRunbookTask, value: any) => void
    onDropdownOpenChange: (open: boolean, rowIndex?: number) => void
    forecastFinishMs: number | null
    copyToClipboard: (text: string, id: string) => void
    copiedId: string | null
    allTasks: (RecoveryRunbookTask & { _index: number })[]
    t: (key: string, opts?: Record<string, unknown>) => string
    readOnly?: boolean
}) {
    const display = getTaskStatusDisplay(task)

    const cardClasses = {
        complete: 'bg-white border-2 border-emerald-200',
        inProgress: 'bg-white border-2 border-blue-300 shadow-lg shadow-blue-100',
        waiting: 'bg-gray-50 border-2 border-gray-200 opacity-75',
        ready: 'bg-white border-2 border-purple-400 shadow-md shadow-purple-100',
        issue: 'bg-white border-2 border-red-200',
    }

    const statusBadgeClasses = {
        complete: 'bg-emerald-100 text-emerald-700',
        inProgress: 'bg-blue-100 text-blue-700',
        waiting: 'bg-amber-200 text-amber-800 font-semibold text-sm px-3 py-1',
        ready: 'bg-purple-100 text-purple-800 font-semibold text-sm px-3 py-1',
        issue: 'bg-red-100 text-red-700',
    }

    const statusLabels: Record<TaskStatusDisplay, string> = {
        complete: t('recoverRequests.taskStatusComplete'),
        inProgress: t('recoverRequests.taskStatusInProgress'),
        waiting: t('recoverRequests.taskStatusWaiting'),
        ready: t('recoverRequests.taskStatusStart'),
        issue: t('recoverRequests.taskStatusIssue'),
    }

    const duration =
        task.duration != null
            ? formatSecondsToDHM(task.duration * 60)
            : '-'

    const handleCardClick = (e: React.MouseEvent) => {
        if ((e.target as HTMLElement).closest('button, [role="combobox"], [data-radix-collection-item], [data-copy-btn]'))
            return
        onExpand()
    }

    const taskId = task.id ?? index
    const taskCopyId = `task-${taskId}`
    const commandsCopyId = `commands-${taskId}`

    return (
        <div
            role="button"
            tabIndex={0}
            onClick={handleCardClick}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onExpand()
                }
            }}
            className={`rounded-xl border p-4 transition-shadow hover:shadow-md cursor-pointer ${cardClasses[display]}`}
            aria-expanded={isExpanded}
        >
            <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                    <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${statusBadgeClasses[display]}`}
                    >
                        {statusLabels[display]}
                    </span>
                    <span className="text-xs text-gray-400 flex-shrink-0">{duration}</span>
                </div>
                <ForecastIndicator task={task} forecastFinishMs={forecastFinishMs} t={t} />
            </div>

            <div className="flex items-start gap-2">
                <p
                    className={`text-sm text-foreground flex-1 min-w-0 ${!isExpanded ? 'line-clamp-1' : ''}`}
                    title={task.task || ''}
                >
                    <TranslatableText text={task.task || t('objects.runbook.task')} />
                </p>
                <button
                    type="button"
                    data-copy-btn
                    onClick={(e) => {
                        e.stopPropagation()
                        copyToClipboard(task.task || '', taskCopyId)
                    }}
                    className="flex-shrink-0 p-1 rounded text-gray-400 hover:text-gray-600 transition-colors"
                    title="Copy task text"
                    aria-label="Copy task text"
                >
                    {copiedId === taskCopyId ? (
                        <Check className="h-4 w-4 text-emerald-500" />
                    ) : (
                        <Copy className="h-4 w-4" />
                    )}
                </button>
            </div>

            {isExpanded && (
                <>
                    <div className="mt-4 pt-4 border-t border-gray-100" />
                    <div className="mt-4 space-y-4">
                        {display === 'waiting' && (() => {
                            const depNos = parseDependencyNumbers(task.dependencies || '')
                            const incompleteDeps = depNos.filter((no) => {
                                const depTask = allTasks.find((a) => Number(a.taskNo) === no)
                                const status = (depTask?.taskStatus || 'Pending').toLowerCase()
                                return status !== 'completed'
                            })
                            if (incompleteDeps.length > 0) {
                                return (
                                    <div className="mb-4">
                                        <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-2">
                                            {t('objects.runbook.dependencies') || 'Dependencies'}
                                        </p>
                                        <p className="text-sm text-amber-700">
                                            {t('objects.runbook.waitingForTasks') || 'Waiting for tasks'}: {incompleteDeps.sort((a, b) => a - b).join(', ')}
                                        </p>
                                    </div>
                                )
                            }
                            return null
                        })()}
                        <div>
                            <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-2">
                                {t('objects.runbook.commands') || 'Commands'}
                            </p>
                            {task.commands?.trim() ? (
                                <div className="flex items-start gap-2">
                                    <pre className="flex-1 min-w-0 rounded-lg bg-gray-900 text-gray-100 p-3 font-mono text-sm whitespace-pre-wrap overflow-x-auto">
                                        {task.commands}
                                    </pre>
                                    <button
                                        type="button"
                                        data-copy-btn
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            copyToClipboard(task.commands || '', commandsCopyId)
                                        }}
                                        className="flex-shrink-0 p-2 rounded text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
                                        title="Copy commands"
                                        aria-label="Copy commands"
                                    >
                                        {copiedId === commandsCopyId ? (
                                            <Check className="h-4 w-4 text-emerald-400" />
                                        ) : (
                                            <Copy className="h-4 w-4" />
                                        )}
                                    </button>
                                </div>
                            ) : (
                                <p className="text-gray-400 italic text-sm">
                                    {t('objects.runbook.noCommands') || 'No commands for this task'}
                                </p>
                            )}
                        </div>
                        <div className="flex flex-wrap gap-2 items-center">
                            {readOnly ? (
                                <>
                                    <div className="flex items-center gap-1.5">
                                        <span className="text-xs text-muted-foreground">
                                            {t('recoverRequests.taskStatus') || 'Status'}:
                                        </span>
                                        <span className="text-xs px-2 py-0.5">
                                            {(task.taskStatus as string) || '—'}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                        <span className="text-xs text-muted-foreground">
                                            {t('recoverRequests.taskOwner') || 'User'}:
                                        </span>
                                        <ReadOnlyOwnerDisplay value={(task.taskOwner as string | null) || null} />
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div onClick={(e) => e.stopPropagation()}>
                                        <EditableStatusCell
                                            value={(task.taskStatus as string | null) || null}
                                            onUpdate={(v) => onTaskChange(index, 'taskStatus', v)}
                                            onDropdownOpenChange={(open) =>
                                                onDropdownOpenChange?.(open, index)
                                            }
                                        />
                                    </div>
                                    <div onClick={(e) => e.stopPropagation()} className="flex items-center gap-1.5">
                                        <span className="text-xs text-muted-foreground">
                                            {t('recoverRequests.taskOwner') || 'User'}:
                                        </span>
                                        <EditableOwnerCell
                                            value={(task.taskOwner as string | null) || null}
                                            onUpdate={(v) => onTaskChange(index, 'taskOwner', v)}
                                            onDropdownOpenChange={(open) =>
                                                onDropdownOpenChange?.(open, index)
                                            }
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    )
}

export function RunbookRecoveryTable({
    tasks,
    forecastStartTime,
    onSave,
    requestID,
    onPauseRefetchChange,
    readOnly = false,
    onTaskIssue,
}: RunbookRecoveryTableProps) {
    const { t, i18n } = useTranslation()
    const [viewMode, setViewMode] = useState<'card' | 'table'>('card')
    const [showFullTasks, setShowFullTasks] = useState(false)
    const [showActualsAndForecast, setShowActualsAndForecast] = useState(true)
    const [localTasks, setLocalTasks] = useState<RecoveryRunbookTask[]>(tasks || [])
    const [dropdownOpen, setDropdownOpen] = useState(false)
    const [dropdownOpenRowIndex, setDropdownOpenRowIndex] = useState<number | null>(null)
    const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set())
    const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const onSaveRef = useRef(onSave)
    onSaveRef.current = onSave
    const hasEditsRef = useRef(false)
    const justSavedRef = useRef(false)
    const [dirtyIndices, setDirtyIndices] = useState<Set<number>>(new Set())
    const [copiedId, setCopiedId] = useState<string | null>(null)

    const copyToClipboard = useCallback(async (text: string, id: string) => {
        await navigator.clipboard.writeText(text)
        setCopiedId(id)
        setTimeout(() => setCopiedId(null), 2000)
    }, [])

    useEffect(() => {
        if (tasks && tasks.length > 0 && dirtyIndices.size === 0 && !dropdownOpen && !justSavedRef.current) {
            setLocalTasks(tasks)
        }
    }, [tasks, dirtyIndices.size, dropdownOpen])

    const handleTaskChange = useCallback((index: number, field: keyof RecoveryRunbookTask, value: any) => {
        if (readOnly) return
        hasEditsRef.current = true
        setDirtyIndices((prev) => new Set(prev).add(index))
        setDropdownOpen(false)
        setDropdownOpenRowIndex(null)
        if (field === 'taskStatus' && value === 'Completed') {
            setExpandedRows((prev) => {
                const next = new Set(prev)
                next.delete(index)
                return next
            })
        }
        setLocalTasks((prev) => {
            const updated = [...prev]
            const task = { ...updated[index], [field]: value } as RecoveryRunbookTask
            if (field === 'taskStatus') {
                const now = truncateToMinuteFloor(new Date())
                // Any status change should set actualStart if not already set
                if (!task.actualStart && (value === 'Started' || value === 'Completed' || value === 'Issue')) {
                    task.actualStart = now.toISOString()
                }
                if (value === 'Started') {
                    // actualStart already set above
                } else if (value === 'Completed') {
                    task.actualFinish = now.toISOString()
                    const actualStart = new Date(task.actualStart!)
                    task.actualDuration = minutesBetween(actualStart, now)
                } else if (value === 'Issue') {
                    task.actualFinish = now.toISOString()
                    const actualStart = new Date(task.actualStart!)
                    task.actualDuration = minutesBetween(actualStart, now)
                    // Notify the AI assistant about the issue in background
                    onTaskIssue?.(task)
                }
            }
            updated[index] = task
            return updated
        })
    }, [readOnly, onTaskIssue])

    useEffect(() => {
        if (!hasEditsRef.current || dirtyIndices.size === 0) return
        if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)
        saveTimeoutRef.current = setTimeout(() => {
            const tasksToSave = localTasks
                .filter((t, idx) => t.id != null && dirtyIndices.has(idx))
                .map((task) => ({
                    id: task.id!,
                    ...(task.taskStatus !== undefined && { taskStatus: task.taskStatus }),
                    ...(task.taskOwner !== undefined && { taskOwner: task.taskOwner }),
                    ...(task.actualStart !== undefined && task.actualStart != null && { actualStart: task.actualStart }),
                    ...(task.actualFinish !== undefined && task.actualFinish != null && { actualFinish: task.actualFinish }),
                    ...(task.actualDuration !== undefined && task.actualDuration != null && { actualDuration: task.actualDuration }),
                    ...(task.duration != null && { duration: task.duration }),
                }))
                .filter((t) => Object.keys(t).length > 1)
            if (tasksToSave.length > 0) {
                onSaveRef.current(tasksToSave)
            }
            setDirtyIndices(new Set())
            hasEditsRef.current = false
            justSavedRef.current = true
            setTimeout(() => { justSavedRef.current = false }, 1500)
        }, 500)
        return () => {
            if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)
        }
    }, [localTasks, dirtyIndices])

    const tasksWithIndex = useMemo(() => {
        if (!localTasks || localTasks.length === 0) return []
        return localTasks.map((task, index) => ({ ...task, _index: index }))
    }, [localTasks])

    useEffect(() => {
        onPauseRefetchChange?.(dirtyIndices.size > 0 || dropdownOpen)
    }, [dirtyIndices.size, dropdownOpen, onPauseRefetchChange])

    const handleDropdownOpenChange = useCallback((open: boolean, rowIndex?: number) => {
        setDropdownOpen(open)
        setDropdownOpenRowIndex(open && rowIndex != null ? rowIndex : null)
    }, [])

    const handleRowExpand = useCallback((index: number) => {
        setExpandedRows((prev) => {
            const next = new Set(prev)
            if (next.has(index)) next.delete(index)
            else next.add(index)
            return next
        })
    }, [])

    const columns = useMemo(
        () =>
            createRunbookRecoveryColumns(
                handleTaskChange,
                t,
                i18n.language,
                forecastStartTime,
                tasksWithIndex,
                showFullTasks,
                handleDropdownOpenChange,
                readOnly
            ),
        [handleTaskChange, t, i18n.language, forecastStartTime, tasksWithIndex, showFullTasks, handleDropdownOpenChange, readOnly]
    )

    const columnVisibility = useMemo(
        () => ({
            taskOffset: false,
            forecastTaskOffset: false,
            ...(showActualsAndForecast
                ? {}
                : {
                      actualStart: false,
                      actualFinish: false,
                      actualDuration: false,
                      forecastStart: false,
                      forecastFinish: false,
                      forecastDuration: false,
                  }),
        }),
        [showActualsAndForecast]
    )

    const table = useReactTable({
        data: tasksWithIndex,
        columns,
        getCoreRowModel: getCoreRowModel(),
        state: { columnVisibility },
        onColumnVisibilityChange: () => {},
    })

    const task1 = useMemo(
        () =>
            tasksWithIndex.length > 0
                ? tasksWithIndex.find((t) => Number(t.taskNo) === 1) ??
                  tasksWithIndex.reduce(
                      (a, b) => ((a.taskOffset ?? 9999) < (b.taskOffset ?? 9999) ? a : b),
                      tasksWithIndex[0]
                  )
                : null,
        [tasksWithIndex]
    )

    const forecastBaseMs = useMemo(() => {
        if (task1?.actualStart) {
            return truncateToMinuteFloor(new Date(task1.actualStart)).getTime()
        }
        return truncateToMinuteFloor(new Date()).getTime()
    }, [task1?.actualStart])

    const forecastTimesMap = useMemo(
        () => computeForecastTimesMap(tasksWithIndex, forecastBaseMs),
        [tasksWithIndex, forecastBaseMs]
    )

    const readyTasks = useMemo(
        () =>
            tasksWithIndex.filter((t) => {
                const status = (t.taskStatus || 'Pending').toLowerCase()
                const waitGo = (t.waitGo || 'WAIT').toUpperCase()
                return status === 'pending' && waitGo === 'GO'
            }),
        [tasksWithIndex]
    )

    if (tasksWithIndex.length === 0) {
        return (
            <div className="rounded-lg border p-8 text-center text-muted-foreground">
                {t('recoverRequests.noRunbook')}
            </div>
        )
    }

    return (
        <div className="mt-6 space-y-4">
            {/* View toggle: Card | Table */}
            <div className="flex flex-wrap items-center gap-4">
                <CardTableViewToggle
                    value={viewMode}
                    onValueChange={setViewMode}
                    cardLabel={t('objects.runbook.viewCard') || 'Card'}
                    tableLabel={t('objects.runbook.viewTable') || 'Table'}
                />
                {viewMode === 'table' && (
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                            <Switch
                                id="show-full-tasks"
                                checked={showFullTasks}
                                onCheckedChange={setShowFullTasks}
                            />
                            <Label htmlFor="show-full-tasks" className="text-sm cursor-pointer">
                                {t('objects.runbook.showFullTasks') || 'Show full tasks'}
                            </Label>
                        </div>
                        <div className="flex items-center gap-2">
                            <Switch
                                id="show-actuals-forecast"
                                checked={showActualsAndForecast}
                                onCheckedChange={setShowActualsAndForecast}
                            />
                            <Label htmlFor="show-actuals-forecast" className="text-sm cursor-pointer">
                                {t('recoverRequests.showActualsAndForecast') || 'Show actuals & forecast'}
                            </Label>
                        </div>
                    </div>
                )}
            </div>

            {/* Pre-Signed URLs — collapsible card */}
            <div style={{ maxWidth: '900px' }}>
                <VersionUrlsTable requestID={requestID} />
            </div>

            {viewMode === 'table' ? (
                <DropdownOpenRowIndexContext.Provider value={dropdownOpenRowIndex}>
                    <ExpandedRowsContext.Provider value={expandedRows}>
                        <div className="rounded-md border overflow-x-auto runbook-recovery-table-wrapper">
                            <Table
                                className="w-full"
                                style={{
                                    tableLayout: 'fixed',
                                    minWidth: table.getVisibleLeafColumns().reduce((sum, col) => sum + (col.id === 'task' ? 320 : col.id === 'commands' ? 200 : col.getSize()), 0),
                                }}
                            >
                                <colgroup>
                                    {table.getVisibleLeafColumns().map((column) => {
                                        const w = column.id === 'task' ? 320 : column.id === 'commands' ? 200 : column.getSize()
                                        return <col key={column.id} style={{ width: w }} />
                                    })}
                                </colgroup>
                                <TableHeader>
                                    {table.getHeaderGroups().map((headerGroup) => (
                                        <TableRow key={headerGroup.id} className="bg-muted/50 border-b">
                                            {headerGroup.headers.map((header) => (
                                                <TableHead
                                                    key={header.id}
                                                    className="font-semibold text-foreground text-xs"
                                                    style={{
                                                        paddingTop: 'var(--table-header-padding-y, 0.75rem)',
                                                        paddingBottom: 'var(--table-header-padding-y, 0.75rem)',
                                                        paddingLeft: 'var(--table-header-padding-x, 1rem)',
                                                        paddingRight: 'var(--table-header-padding-x, 1rem)',
                                                        width: header.getSize() !== 150 ? `${header.getSize()}px` : undefined,
                                                        minWidth: header.column.id === 'task' ? 320 : header.column.id === 'commands' ? 200 : undefined,
                                                    }}
                                                >
                                                    {header.isPlaceholder
                                                        ? null
                                                        : flexRender(header.column.columnDef.header, header.getContext())}
                                                </TableHead>
                                            ))}
                                        </TableRow>
                                    ))}
                                </TableHeader>
                                <TableBody>
                                    {table.getRowModel().rows?.length ? (
                                        table.getRowModel().rows.map((row) => (
                                            <TableRow
                                                key={row.id}
                                                className="hover:bg-muted/50 cursor-pointer"
                                                onClick={(e) => {
                                                    if ((e.target as HTMLElement).closest('button, [role="combobox"], [data-radix-collection-item]')) return
                                                    handleRowExpand(row.original._index)
                                                }}
                                            >
                                                {row.getVisibleCells().map((cell) => (
                                                    <TableCell
                                                        key={cell.id}
                                                        style={{
                                                            paddingTop: 'var(--table-cell-padding-y, 0.5rem)',
                                                            paddingBottom: 'var(--table-cell-padding-y, 0.5rem)',
                                                            paddingLeft: 'var(--table-header-padding-x, 1rem)',
                                                            paddingRight: 'var(--table-header-padding-x, 1rem)',
                                                            width: cell.column.getSize() !== 150 ? `${cell.column.getSize()}px` : undefined,
                                                            minWidth: cell.column.id === 'task' ? 320 : cell.column.id === 'commands' ? 200 : undefined,
                                                            overflow: 'visible',
                                                            verticalAlign: 'top',
                                                        }}
                                                        className="overflow-visible"
                                                    >
                                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={columns.length} className="h-24 text-center">
                                                {t('common.noResults')}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                        <style>{`
                            .runbook-recovery-table-wrapper table { table-layout: fixed !important; }
                        `}</style>
                    </ExpandedRowsContext.Provider>
                </DropdownOpenRowIndexContext.Provider>
            ) : (
                <div className="max-w-3xl">
            {readyTasks.length > 0 && (
                <div className="rounded-xl border-2 border-purple-300 bg-purple-50/80 p-4 shadow-md shadow-purple-100 mb-8">
                    <h3 className="text-sm font-semibold text-purple-800 mb-3">
                        {readyTasks.length === 1
                            ? t('objects.runbook.taskReady') || 'Task Ready'
                            : `${t('objects.runbook.readyToStart') || 'Tasks Ready'} (${readyTasks.length} tasks)`}
                    </h3>
                    {readyTasks.length <= 3 ? (
                        <div className="space-y-2">
                            {readyTasks.map((task) => {
                                const idx = task._index
                                return (
                                    <div
                                        key={task.id ?? idx}
                                        className="flex items-center justify-between gap-3 py-2 px-3 rounded-lg bg-white border-2 border-purple-200"
                                    >
                                        <div className="flex items-center gap-2 min-w-0 flex-1">
                                            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-500 text-white text-xs font-semibold flex items-center justify-center">
                                                {task.taskNo ?? '-'}
                                            </span>
                                            <span className="text-sm text-foreground truncate">
                                                <TranslatableText text={task.task || t('objects.runbook.task')} />
                                            </span>
                                        </div>
                                        {!readOnly && (
                                            <Button
                                                size="sm"
                                                className="bg-purple-600 hover:bg-purple-700 flex-shrink-0"
                                                onClick={() =>
                                                    handleTaskChange(idx, 'taskStatus', 'Started')
                                                }
                                            >
                                                {t('objects.runbook.startTask') || 'Start'}
                                            </Button>
                                        )}
                                    </div>
                                )
                            })}
                        </div>
                    ) : (
                        <div className="flex items-center justify-between gap-3">
                            <p className="text-sm text-purple-800">
                                {readyTasks.length} tasks ready to start
                            </p>
                            {!readOnly && (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="border-purple-300 text-purple-700 hover:bg-purple-100"
                                    onClick={() => {
                                        const firstReadyIdx = tasksWithIndex.findIndex(
                                            (t) =>
                                                (t.taskStatus || 'Pending').toLowerCase() ===
                                                    'pending' &&
                                                (t.waitGo || 'WAIT').toUpperCase() === 'GO'
                                        )
                                        if (firstReadyIdx >= 0) {
                                            setExpandedRows((prev) => new Set(prev).add(firstReadyIdx))
                                            document
                                                .getElementById(`timeline-task-${firstReadyIdx}`)
                                                ?.scrollIntoView({ behavior: 'smooth' })
                                        }
                                    }}
                                >
                                    {t('objects.runbook.viewAll') || 'View All'}
                                </Button>
                            )}
                        </div>
                    )}
                </div>
            )}

            <div className="relative pl-6">
                {/* Timeline line - centered through nodes (pl-6=24px + node radius 20px = 44px) */}
                <div
                    className="absolute left-[43px] top-5 bottom-5 w-0.5 bg-gray-200"
                    aria-hidden
                />

                <div className="space-y-4">
                    {tasksWithIndex.map((task, idx) => {
                        const isExpanded =
                            expandedRows.has(idx) || dropdownOpenRowIndex === idx
                        const taskNo = task.taskNo != null ? Number(task.taskNo) : null
                        const forecast = taskNo != null ? forecastTimesMap.get(taskNo) : undefined
                        const forecastFinishMs = forecast?.forecastFinishMs ?? null
                        return (
                            <div
                                key={task.id ?? idx}
                                id={`timeline-task-${idx}`}
                                className="relative flex gap-4 items-start"
                            >
                                <TimelineNode
                                    task={task}
                                    index={idx}
                                    onTaskChange={handleTaskChange}
                                    t={t}
                                    readOnly={readOnly}
                                />
                                <div className="flex-1 min-w-0">
                                    <TaskCard
                                        task={task}
                                        index={idx}
                                        isExpanded={isExpanded}
                                        onExpand={() => handleRowExpand(idx)}
                                        onTaskChange={handleTaskChange}
                                        onDropdownOpenChange={handleDropdownOpenChange}
                                        forecastFinishMs={forecastFinishMs}
                                        copyToClipboard={copyToClipboard}
                                        copiedId={copiedId}
                                        allTasks={tasksWithIndex}
                                        t={t}
                                        readOnly={readOnly}
                                    />
                                </div>
                            </div>
                        )
                    })}
                </div>
            </div>
                </div>
            )}
        </div>
    )
}
